<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>
  
    <div class="column-right">
      <div class="main-container">
        <h2>Panel de Estadísticas <img :src="admin_datos.empresa && admin_datos.empresa.atributos && admin_datos.empresa.atributos.logo_filename ? admin_datos.empresa.atributos.logo_filename : require('@/assets/logo-jobbing-nuevo.webp')" style="height:40px; vertical-align: middle;" alt="Logo"></h2>
        <p>Analiza el rendimiento de tu operación en un período específico.</p>
  
        <hr>
  
        <!-- Date Range Selection -->
        <div class="date-selector">
           <!-- ... (date inputs and button remain the same) ... -->
           <div class="date-input">
             <label for="startDate">Fecha Inicio:</label>
             <input type="date" id="startDate" v-model="startDate" class="form-control form-control-sm">
           </div>
           <div class="date-input">
             <label for="endDate">Fecha Fin:</label>
             <input type="date" id="endDate" v-model="endDate" class="form-control form-control-sm">
           </div>
           <button @click="fetchStatistics" :disabled="isLoading || !startDate || !endDate" class="btn btn-primary btn-sm">
             <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
             {{ isLoading ? 'Cargando...' : 'Actualizar Estadísticas' }}
           </button>
        </div>
  
        <!-- Loading and Error States -->
         <!-- ... (v-if loading/error remain the same) ... -->
        <div v-if="isLoading && !statsData" class="loading-message">
          Cargando datos... <span class="spinner-border spinner-border-sm"></span>
        </div>
        <div v-if="error" class="alert alert-danger error-message">
          Error al cargar estadísticas: {{ error }}
        </div>
  
  
        <!-- Statistics Display Area -->
        <div v-if="!isLoading && statsData" class="statistics-content">
  
          <!-- Summary Cards -->
          <!-- ... (summary cards remain the same) ... -->
          <div class="summary-cards">
            <div class="summary-card">
              <h4>Pagos Recibidos</h4>
              <p class="summary-value">{{ formatCurrency(statsData.payments.summary.grand_total_amount) }}</p>
              <p class="summary-detail">{{ statsData.payments.summary.total_count }} transacciones</p>
            </div>
             <div class="summary-card">
              <h4>Presupuestos Enviados</h4>
              <p class="summary-value">{{ formatCurrency(statsData.budgets_sent.summary.grand_total_value) }}</p>
              <p class="summary-detail">{{ statsData.budgets_sent.summary.total_count }} presupuestos</p>
            </div>
            <div class="summary-card">
              <h4>Órdenes Creadas</h4>
              <p class="summary-value">{{ statsData.orders_created.summary.total_count }}</p>
               <p class="summary-detail">Total en período</p>
            </div>
            <div class="summary-card">
              <h4>Calificación Prom.</h4>
              <p class="summary-value">{{ statsData.ratings.summary.overall_average_rating.toFixed(1) }} ★</p>
              <p class="summary-detail">{{ statsData.ratings.summary.total_count }} calificaciones</p>
            </div>
          </div>
  
  
          <hr>
  
          <!-- Charts Area -->
          <div class="charts-grid">
            <div class="chart-container">
              <h4>Pagos Diarios</h4>
              <!-- *** CHANGE: Use dual axis options *** -->
              <line-chart v-if="chartDataPayments.labels.length" :chart-data="chartDataPayments" :options="chartOptionsLineDualAxis"></line-chart>
              <p v-else class="no-data">No hay datos de pagos para este período.</p>
            </div>
  
            <div class="chart-container">
              <h4>Presupuestos Enviados Diarios</h4>
               <!-- *** CHANGE: Use dual axis options *** -->
              <line-chart v-if="chartDataBudgets.labels.length" :chart-data="chartDataBudgets" :options="chartOptionsLineDualAxis"></line-chart>
               <p v-else class="no-data">No hay datos de presupuestos para este período.</p>
            </div>
  
            <div class="chart-container">
              <h4>Órdenes Creadas Diarias</h4>
               <!-- *** CHANGE: Use standard single axis options *** -->
              <line-chart v-if="chartDataOrders.labels.length" :chart-data="chartDataOrders" :options="chartOptionsLineSingleAxis"></line-chart>
               <p v-else class="no-data">No hay datos de órdenes creadas para este período.</p>
            </div>
  
            <div class="chart-container">
              <h4>Calificaciones Diarias</h4>
               <!-- *** CHANGE: Use dual axis options (Avg Rating vs Count) *** -->
              <line-chart v-if="chartDataRatings.labels.length" :chart-data="chartDataRatings" :options="chartOptionsLineDualAxisRatings"></line-chart>
              <p v-else class="no-data">No hay datos de calificaciones para este período.</p>
            </div>
  
             <div class="chart-container chart-container-small">
                <h4>Distribución de Estados de Órdenes</h4>
                <doughnut-chart v-if="chartDataStatus.labels.length" :chart-data="chartDataStatus" :options="chartOptionsDoughnut"></doughnut-chart>
                <p v-else class="no-data">No hay datos de estado de órdenes.</p>
              </div>
          </div>
  
        </div>
         <div v-if="!isLoading && !statsData && !error" class="loading-message">
           Selecciona un rango de fechas y presiona "Actualizar Estadísticas".
         </div>
  
      </div>
    </div>
  </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import SideNav from '@/components/SideNav.vue';
  import TopNav from '@/components/TopNav.vue';
  import LineChart from '@/components/charts/LineChart.js';
  import DoughnutChart from '@/components/charts/DoughnutChart.js';
  
  // --- Helpers (getISODateString, generateDateRange) remain the same ---
  function getISODateString(date) {
    const offset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() - offset);
    return adjustedDate.toISOString().split('T')[0];
  }
  
  function generateDateRange(startDateStr, endDateStr) {
    const dates = [];
    let currentDate = new Date(startDateStr + 'T00:00:00');
    const endDate = new Date(endDateStr + 'T00:00:00');
    if (isNaN(currentDate.getTime()) || isNaN(endDate.getTime())) { return []; }
    if (currentDate > endDate) { return []; }
    while (currentDate <= endDate) {
      dates.push(getISODateString(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }
  
  
  export default {
    name: 'StatisticsDashboard',
    components: {
      SideNav,
      TopNav,
      LineChart,
      DoughnutChart,
    },
    data() {
      // --- Base Options for Interaction ---
      const baseLineOptions = {
          responsive: true,
          maintainAspectRatio: false,
          spanGaps: false, // Important for gaps with null data
          tooltips: { mode: 'index', intersect: false },
          hover: { mode: 'index', intersect: false },
          legend: { display: true },
          scales: {
               xAxes: [{
                  ticks: {
                      autoSkip: true,
                      maxTicksLimit: 15,
                  }
              }]
              // yAxes will be defined in specific options below
          }
      };
  
      return {
        isLoading: false,
        error: null,
        startDate: '',
        endDate: '',
        statsData: null,
  
        chartDataPayments: { labels: [], datasets: [] },
        chartDataBudgets: { labels: [], datasets: [] },
        chartDataOrders: { labels: [], datasets: [] },
        chartDataRatings: { labels: [], datasets: [] },
        chartDataStatus: { labels: [], datasets: [] },
  
        // --- NEW: Specific Options Objects ---
  
        // Options for charts with a single Y axis (like Orders Created)
        chartOptionsLineSingleAxis: {
          ...baseLineOptions, // Inherit base options
          scales: {
              ...baseLineOptions.scales, // Inherit xAxes
              yAxes: [{
                  id: 'y-axis-main', // Give it an ID even if single
                  position: 'left',
                  ticks: { beginAtZero: true }
              }]
          }
        },
  
        // Options for charts with Amount ($) on left, Count on right
        chartOptionsLineDualAxis: {
          ...baseLineOptions, // Inherit base options
          scales: {
              ...baseLineOptions.scales, // Inherit xAxes
              yAxes: [
                  // Left Axis (Amount/Value)
                  {
                      id: 'y-axis-amount', // Specific ID
                      type: 'linear', // Explicitly linear
                      position: 'left',
                      ticks: {
                          beginAtZero: true,
                          // Optional: Callback to format as currency
                          callback: function(value, index, values) {
                               if (parseInt(value) >= 1000) {
                                  return '$' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                               } else {
                                  return '$' + value;
                               }
                          }
                      },
                      scaleLabel: {
                          display: true,
                          labelString: 'Monto ($)'
                      }
                  },
                  // Right Axis (Count)
                  {
                      id: 'y-axis-count', // Specific ID
                      type: 'linear',
                      position: 'right',
                      ticks: {
                          beginAtZero: true,
                           stepSize: 1 // Ensure integer steps for counts if needed
                      },
                       scaleLabel: {
                          display: true,
                          labelString: 'Cantidad'
                      },
                      // Prevent grid lines from this axis cluttering the chart
                      gridLines: {
                          drawOnChartArea: false,
                      },
                  }
              ]
          }
        },
  
         // Options for Ratings chart (Avg on left, Count on right)
        chartOptionsLineDualAxisRatings: {
          ...baseLineOptions, // Inherit base options
          scales: {
              ...baseLineOptions.scales, // Inherit xAxes
              yAxes: [
                  // Left Axis (Average Rating)
                  {
                      id: 'y-axis-rating', // Specific ID
                      type: 'linear',
                      position: 'left',
                      ticks: {
                          beginAtZero: true,
                          // Suggested Min/Max for ratings (adjust if needed)
                           suggestedMin: 0,
                           suggestedMax: 5,
                           stepSize: 1
                      },
                      scaleLabel: {
                          display: true,
                          labelString: 'Calificación Prom. (★)'
                      }
                  },
                  // Right Axis (Count)
                  {
                      id: 'y-axis-count', // Specific ID (can reuse if scale is similar)
                      type: 'linear',
                      position: 'right',
                      ticks: {
                          beginAtZero: true,
                           stepSize: 1
                      },
                       scaleLabel: {
                          display: true,
                          labelString: 'Cantidad'
                      },
                      gridLines: {
                          drawOnChartArea: false,
                      },
                  }
              ]
          }
        },
  
  
        chartOptionsDoughnut: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'right' }
        }
      }
    },
    computed:{
      ...mapGetters(['admin_datos']),
      apiUrl() {
          const host = typeof window !== 'undefined' && window.hostname ? window.hostname : '';
          if (!host) { console.warn("window.hostname is not defined."); return '/api/property_manager/statistics'; }
          const baseApiUrl = `${host.replace(/\/$/, '')}/api/property_manager/statistics`;
          return baseApiUrl;
      }
    },
    methods: {
      setDefaultDates() { /* ... same ... */
          const today = new Date();
          const endDate = new Date(today);
          const startDate = new Date(today);
          startDate.setDate(today.getDate() - 29);
          this.startDate = getISODateString(startDate);
          this.endDate = getISODateString(endDate);
      },
  
      async fetchStatistics() { /* ... same ... */
           if (!this.startDate || !this.endDate) { this.error = "Por favor selecciona fecha de inicio y fin."; return; }
           if (new Date(this.startDate) > new Date(this.endDate)) { this.error = "La fecha de inicio no puede ser posterior a la fecha de fin."; return; }
           this.isLoading = true; this.error = null;
           try {
              const response = await axios.get(this.apiUrl, { params: { start_date: this.startDate, end_date: this.endDate } });
              this.statsData = response.data;
              this.prepareChartData();
           } catch (err) {
              console.error("Error fetching statistics:", err); this.statsData = null;
              if (err.response && err.response.data && err.response.data.error) { this.error = err.response.data.error + (err.response.data.details ? ` (${err.response.data.details})` : ''); }
              else if (err.request) { this.error = "No se pudo conectar al servidor."; }
              else { this.error = "Ocurrió un error inesperado."; }
              this.clearChartData();
           } finally { this.isLoading = false; }
      },
  
  
      prepareChartData() {
          if (!this.statsData || !this.startDate || !this.endDate) { this.clearChartData(); return; }
          const fullDateRange = generateDateRange(this.startDate, this.endDate);
          if (!fullDateRange.length) { this.clearChartData(); return; }
          const createDataMap = (timeSeries) => { /* ... same ... */
               const map = new Map();
               if (timeSeries && Array.isArray(timeSeries)) { timeSeries.forEach(item => { if (item.date) { map.set(item.date, item); } }); }
               return map;
          };
  
          // --- Process Payments ---
          const paymentsMap = createDataMap(this.statsData.payments?.time_series);
          const paymentAmounts = fullDateRange.map(date => paymentsMap.get(date)?.total_amount ?? 0);
          const paymentCounts = fullDateRange.map(date => paymentsMap.get(date)?.count ?? 0);
          this.chartDataPayments = {
              labels: fullDateRange,
              datasets: [
                  {
                      label: 'Monto Pagado ($)', data: paymentAmounts,
                      borderColor: '#36A2EB', backgroundColor: 'rgba(54, 162, 235, 0.1)', fill: true,
                      yAxisID: 'y-axis-amount' // *** Link to left axis ***
                  },
                  {
                      label: 'Nº Pagos', data: paymentCounts,
                      borderColor: '#FFCE56', backgroundColor: 'rgba(255, 206, 86, 0.1)', fill: true,
                      yAxisID: 'y-axis-count' // *** Link to right axis ***
                  }
              ]
          };
  
           // --- Process Budgets Sent ---
           const budgetsMap = createDataMap(this.statsData.budgets_sent?.time_series);
           const budgetValues = fullDateRange.map(date => budgetsMap.get(date)?.total_value ?? 0);
           const budgetCounts = fullDateRange.map(date => budgetsMap.get(date)?.count ?? 0);
           this.chartDataBudgets = {
               labels: fullDateRange,
               datasets: [
                   {
                      label: 'Valor Total ($)', data: budgetValues,
                      borderColor: '#4BC0C0', backgroundColor: 'rgba(75, 192, 192, 0.1)', fill: true,
                      yAxisID: 'y-axis-amount' // *** Link to left axis (reuse ID okay if scale concept is similar) ***
                   },
                   {
                      label: 'Nº Presupuestos', data: budgetCounts,
                      borderColor: '#9966FF', backgroundColor: 'rgba(153, 102, 255, 0.1)', fill: true,
                       yAxisID: 'y-axis-count' // *** Link to right axis ***
                   }
               ]
           };
  
          // --- Process Orders Created ---
          const ordersMap = createDataMap(this.statsData.orders_created?.time_series);
          const orderCounts = fullDateRange.map(date => ordersMap.get(date)?.count ?? 0);
          this.chartDataOrders = {
              labels: fullDateRange,
              datasets: [{
                  label: 'Órdenes Creadas', data: orderCounts,
                  borderColor: '#FF6384', backgroundColor: 'rgba(255, 99, 132, 0.1)', fill: true,
                  // yAxisID: 'y-axis-main' // Optional: Explicitly link to the single axis ID if defined
              }]
          };
  
          // --- Process Ratings ---
          const ratingsMap = createDataMap(this.statsData.ratings?.time_series);
          // Use null for average gaps, 0 for count gaps
          const ratingAverages = fullDateRange.map(date => ratingsMap.get(date)?.average_rating ?? null);
          const ratingCounts = fullDateRange.map(date => ratingsMap.get(date)?.count ?? 0);
          this.chartDataRatings = {
               labels: fullDateRange,
               datasets: [
                  {
                      label: 'Calificación Promedio (★)', data: ratingAverages,
                      borderColor: '#FF9F40', backgroundColor: 'rgba(255, 159, 64, 0.1)', fill: false,
                      yAxisID: 'y-axis-rating' // *** Link to left rating axis ***
                  },
                  {
                      label: 'Nº Calificaciones', data: ratingCounts,
                      borderColor: '#C9CBCF', backgroundColor: 'rgba(201, 203, 207, 0.1)', fill: true,
                      yAxisID: 'y-axis-count' // *** Link to right count axis ***
                  }
               ]
           };
  
          // --- Process Order Status Summary ---
           const statusSummary = this.statsData.order_status_summary;
           if (statusSummary && Object.keys(statusSummary).length > 0) { /* ... same ... */
               const labels = Object.keys(statusSummary); const data = Object.values(statusSummary);
               const backgroundColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF'];
               this.chartDataStatus = { labels: labels, datasets: [{ label: 'Distribución de Estados', data: data, backgroundColor: backgroundColors.slice(0, data.length), hoverOffset: 4 }] };
           } else {
               this.chartDataStatus = { labels: [], datasets: [] };
           }
      },
  
      clearChartData() { /* ... same ... */
           this.chartDataPayments = { labels: [], datasets: [] }; this.chartDataBudgets = { labels: [], datasets: [] }; this.chartDataOrders = { labels: [], datasets: [] }; this.chartDataRatings = { labels: [], datasets: [] }; this.chartDataStatus = { labels: [], datasets: [] };
      },
  
      formatCurrency(value) { /* ... same ... */
        if (typeof value !== 'number') { return '$0'; }
        return '$' + Math.round(value).toLocaleString('es-CL');
      }
    },
    mounted() { /* ... same ... */
      this.setDefaultDates();
      this.fetchStatistics();
    }
  }
  </script>
  
  <!-- Styles remain the same -->
  <style scoped>
  /* ... All styles from previous answer ... */
  </style>
  
  <style scoped>
  /* Reuse styles from your example */
  .column-right{
    background-color: rgb(242, 247, 251);
    padding-top: 80px; /* Adjust if TopNav height changes */
    padding-left: 260px; /* Adjust if SideNav width changes */
    width: 100%;
    min-height: 100vh; /* Ensure it fills height */
    box-sizing: border-box; /* Include padding in width calculation */
  }
  .main-container{
    background-color: white;
    margin: 15px; /* Add some margin around */
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  h2 {
      margin-bottom: 10px;
      color: #333;
  }
  p {
      color: #555;
      margin-bottom: 15px;
  }
  hr {
      margin-top: 20px;
      margin-bottom: 20px;
      border-top: 1px solid #eee;
  }
  
  /* Date Selection Styles */
  .date-selector {
      display: flex;
      flex-wrap: wrap; /* Allow wrapping on smaller screens */
      align-items: flex-end; /* Align items to bottom */
      gap: 15px; /* Space between elements */
      margin-bottom: 25px;
  }
  .date-input {
      display: flex;
      flex-direction: column;
  }
  .date-input label {
      font-size: 0.9em;
      margin-bottom: 3px;
      color: #666;
  }
  /* Assuming Bootstrap for form-control styling, adjust if not using */
  .form-control-sm {
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
      border-radius: 0.2rem;
      border: 1px solid #ced4da;
  }
  .btn-sm {
       padding: 0.25rem 0.75rem;
       font-size: 0.875rem;
       line-height: 1.5;
       border-radius: 0.2rem;
  }
  .btn-primary {
      color: #fff;
      background-color: #007bff;
      border-color: #007bff;
      cursor: pointer;
  }
  .btn:disabled {
      opacity: 0.65;
      cursor: not-allowed;
  }
  
  /* Loading / Error Messages */
  .loading-message, .error-message {
      text-align: center;
      padding: 20px;
      margin-top: 20px;
      border-radius: 5px;
  }
  .error-message {
      color: #721c24;
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
  }
  .loading-message {
      color: #004085;
      background-color: #cce5ff;
      border: 1px solid #b8daff;
  }
  
  /* Statistics Content Layout */
  .statistics-content {
      margin-top: 20px;
  }
  
  /* Summary Cards */
  .summary-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); /* Responsive grid */
      gap: 20px;
      margin-bottom: 30px;
  }
  .summary-card {
      background-color: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 15px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .summary-card h4 {
      margin-top: 0;
      margin-bottom: 8px;
      font-size: 1em;
      color: #495057;
  }
  .summary-value {
      font-size: 1.6em;
      font-weight: bold;
      color: #212529;
      margin-bottom: 5px;
  }
  .summary-detail {
      font-size: 0.9em;
      color: #6c757d;
      margin-bottom: 0;
  }
  
  
  /* Charts Layout */
  .charts-grid {
      display: grid;
      /* Create 2 columns by default */
      grid-template-columns: repeat(2, 1fr);
      gap: 30px; /* Space between charts */
  }
  .chart-container {
      border: 1px solid #eee;
      border-radius: 8px;
      padding: 20px;
      background-color: #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      /* Give charts a minimum height */
      min-height: 350px;
      display: flex;
      flex-direction: column;
  }
  .chart-container h4 {
       text-align: center;
       margin-top: 0;
       margin-bottom: 15px;
       font-size: 1.1em;
       color: #333;
  }
  /* Adjust doughnut chart container size */
  .chart-container-small {
      min-height: 300px;
      /* Optionally make it span full width on smaller grids */
      /* grid-column: span 2; */ /* Uncomment if you want it full width below other charts */
  }
  
  .no-data {
      text-align: center;
      color: #888;
      margin-top: 40px; /* Center vertically */
      flex-grow: 1; /* Take remaining space */
      display: flex;
      align-items: center;
      justify-content: center;
  }
  
  /* Responsive Adjustments */
  @media (max-width: 1200px) {
      .charts-grid {
          grid-template-columns: 1fr; /* Stack charts into a single column */
      }
       .chart-container-small {
          grid-column: auto; /* Reset span if it was set */
       }
  }
  
  @media (max-width: 800px) {
   .column-right{
      padding-left:0px; /* Collapse padding when SideNav hides */
   }
   .main-container {
      margin: 10px; /* Reduce margin */
   }
   .date-selector {
      flex-direction: column; /* Stack date inputs */
      align-items: stretch; /* Make inputs full width */
   }
   .date-selector button {
      margin-top: 10px;
   }
   .summary-cards {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); /* Smaller cards */
      gap: 15px;
   }
  }
  
  /* Spinner animation */
  @keyframes spinner-border {
    to { transform: rotate(360deg); }
  }
  .spinner-border {
    display: inline-block;
    width: 1rem; /* Adjust size */
    height: 1rem;
    vertical-align: text-bottom;
    border: .2em solid currentColor; /* Adjust thickness */
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border .75s linear infinite;
  }
  .spinner-border-sm {
    width: 0.8rem;
    height: 0.8rem;
    border-width: .15em;
  }
  </style>